import { useEffect, useMemo, useState, useCallback } from "react";
import useActions from "../../../hooks/service-actions.hook";
import useUserDetails from "../../../hooks/user-details.hook";
import { CategoryEffectActions } from "../../../common/services/category/category.service";
import { CategorySortOrderEffectActions } from "../../../common/services/category-sort-order/category-sort-order.service";
import { useEntity } from "../../../hooks/use-entity.hook";
import { Flaggers } from "../../../common/store/flaggers/flaggers.enum";
import { Entities } from "../../../common/store/entities/entities.enum";
import useLoaders from "../../../hooks/use-loaders.hook";
import { Loaders } from "../../../common/store/loaders/loaders.enum";
import { categoryUtils } from "../../../Utils/category.utils";
import visibilityUtils from "../../../Utils/visibility.utils";
import { CategoriesColorSequence } from "../../../constants/category.constants";
import { FlaggersMethods } from "../../../common/store/flaggers/flaggers.methods";


window["__react-beautiful-dnd-disable-dev-warnings"] = true;

const useTechnologySpendState = ({ isForStorefront = false }) => {
  const { userDetails } = useUserDetails();
  const clientId = userDetails?.objId_Client;

  const [modals, setModals] = useState({
    updateCategories: null,
    viewProducts: null,
  });

  const [state, setState] = useState({
    order: null,
    defineOrder: null,
    categories: [],
    search: ""
  });

  const { defineOrder, order, categories, search } = state;

  const { getAllCategories, updateCategory, deleteCategory } = useActions(CategoryEffectActions);
  const { getCategoriesSortOrder, updateCategoriesSortOrder } = useActions(CategorySortOrderEffectActions);
  const { entityData: deleteCategoryFlagger, setEntityData: setDeleteCategoryFlagger } = useEntity({ entityType: Flaggers.DeleteCategoryFlagger });
  const { entityData: deleteCategoryPromptFlagger, setEntityData: setDeleteCategoryPromptFlagger } = useEntity({ entityType: Flaggers.DeleteCategoryPromptFlagger });
  const { entityData: categoriesFromStore } = useEntity({ entityType: Entities.Category, entityId: clientId });
  const { entityData: categoriesSortOrderFromStore } = useEntity({ entityType: Entities.CategorySortOrder, entityId: clientId });
  const { isLoading: isLoadingCategories } = useLoaders({ loaders: [Loaders.GetCategoriesLoader, Loaders.GetCategoriesSortOrderLoader] });
  const { isLoading: isLoadingDeleteCategory } = useLoaders({ loaders: [Loaders.DeleteCategoryLoader] });

  const categoriesSortOrder = useMemo(() => 
    categoriesSortOrderFromStore?.[defineOrder?.value] ?? []
  , [categoriesSortOrderFromStore, defineOrder?.value]);

  const mapCategory = (category, sortOrderData) => {
    const subCategories = category.SubCatList?.map((subCategory) => {
      const subSortOrder = sortOrderData?.SubCatList?.find((item) => item.Id === subCategory.Id)?.SortOrder;
      return {
        ...subCategory,
        SortOrder: subSortOrder,
        parentCategoryId: category.Id,
      }
    });

    return categoryUtils.fromBEtoUIModel({
      ...category,
      SortOrder: sortOrderData?.SortOrder ?? category?.SortOrder ?? 0,
      SubCatList: subCategories,
    });
  }

  const mappedCategories = useMemo(() => {
    if (!categoriesFromStore) return [];
    
    return Object.values(categoriesFromStore)
      .map((category) => {
        const sortOrderData = categoriesSortOrder?.find((item) => item.Id === category.Id);
        return mapCategory(category, sortOrderData);
      })
      .sort((a, b) => a.SortOrder - b.SortOrder);
  }, [categoriesFromStore, categoriesSortOrder]);

  const filteredCategories = useMemo(() => {
    const filtered = categories;
    return filtered;
  }, [categories, isForStorefront, search]);

  const handleDragEnd = (result, list, type) => {
    const resultList = visibilityUtils.handleDragEnd(result, list);
    if (type === 'Category') {
      setState(prev => ({ ...prev, categories: resultList }));
    }
    updateCategoriesSortOrder({
      clientId,
      type: defineOrder?.value,
      list: resultList.map((item) => ({
        SortOrder: item.SortOrder,
        CategoryId: item.categoryId,
        SubCatList: item.subCategories?.map((sub) => ({
          SortOrder: sub.SortOrder,
          CategoryId: sub.categoryId,
        })) ?? [],
      })),
    });
  };

  const handleSubCategoryDragEnd = (resultList) => {
    updateCategoriesSortOrder({
      clientId,
      type: defineOrder?.value,
      list: resultList.map((item) => ({
        SortOrder: item.SortOrder,
        CategoryId: item.categoryId,
      })),
    });
  }

  const handleSortOrderChange = useCallback((newSortOrder) => {
    setState(prev => ({ ...prev, defineOrder: newSortOrder, order: null }));
    document.body.click();
  }, []);

  const handleOrderChange = useCallback((newOrder) => {
    setState(prev => ({ ...prev, order: newOrder, defineOrder: null }));
  }, []);

  const onViewProductsClick = useCallback((category) => {
    setModals(prev => ({
      ...prev,
      viewProducts: {
        show: true,
        category,
      }
    }));
  }, []);

  const onCategoryUpdate = useCallback((parentCategory, category, type) => {
    const payload = categoryUtils.fromUItoBEModel(category);
    updateCategory({
      category: {
        ...payload,
        Id: category?.newCreated ? undefined : category?.categoryId,
        Type: type,
        ...(parentCategory ? {
          ObjdId_PSA_Product_Category: parentCategory?.categoryId
        } : {})
      },
      clientId,
      IdToClone: category?.IdToClone,
    });
  }, [clientId, updateCategory]);

  const onCategoryDuplicate = useCallback((categoryId) => {
    updateCategory({
      clientId,
      IdToClone: categoryId,
    });
  }, [clientId, updateCategory]);

  const getNewCategoryColor = () => {
    const colorFromList = CategoriesColorSequence?.find((color) => {
      return !categories?.some((category) => {
        return category?.categoryHexColor?.toLowerCase() === color?.toLowerCase();
      });
    });
    if (!colorFromList) {
      return "#000000";
    }
    return colorFromList;
  }

  const handleAddCategory = useCallback(() => {
    const categoryId = Math.random();
    const findTheLowestSortOrder = categories.reduce((min, category) => {
      return Math.min(min, category.SortOrder);
    }, 0);

    const newCategory = {
      categoryId,
      categoryName: "New Category",
      categoryHexColor: getNewCategoryColor(),
      SortOrder: findTheLowestSortOrder - 1,
      Visibility: true,
      isEditing: true,
      isCustom: true,
      newCreated: true,
      storefrontActive: true,
      techSpendActive: true,
    };
    setState(prev => ({ 
      ...prev, 
      categories: [newCategory, ...prev.categories]
    }));
    setTimeout(() => {
      const input = document.getElementById(`category-name-inp-${categoryId}`);
      input?.focus();
    }, 100);
  }, [categories]);

  const refreshData = () => {
    getAllCategories({ clientId, sortColumn: order?.sortColumn, sortOrder: order?.sortOrder });
    if (defineOrder) {
      getCategoriesSortOrder({
        clientId,
        type: defineOrder.value,
      });
    }
  }

  const handleSearchChange = useCallback((e) => {
    setState(prev => ({ ...prev, search: e.target.value }));
  }, []);

  const handleSearchClear = useCallback(() => {
    setState(prev => ({ ...prev, search: "" }));
  }, []);

  useEffect(() => {
    if (!defineOrder) return;
    getCategoriesSortOrder({
      clientId,
      type: defineOrder.value,
    });
  }, [defineOrder, getCategoriesSortOrder, clientId]);
  
  useEffect(() => {
    setState(prev => ({ ...prev, categories: mappedCategories }));
  }, [mappedCategories]);

  useEffect(() => {
    getAllCategories({ clientId, sortColumn: order?.sortColumn, sortOrder: order?.sortOrder });
  }, [getAllCategories, clientId, order]);

  useEffect(() => {
    if (!deleteCategoryFlagger) return;
    if (deleteCategoryFlagger.showModal) {
      const category = categories.find((item) => item.categoryId === deleteCategoryFlagger.categoryId);
      setModals(prev => ({ 
        ...prev, 
        updateCategories: { 
          show: true, category, 
          clientList: deleteCategoryFlagger.clientList, 
          productList: deleteCategoryFlagger.productList,
          CategoryList: deleteCategoryFlagger.CategoryList,
        } 
      }));
      setDeleteCategoryFlagger(null, FlaggersMethods.UpdateFlagger);
    }
  }, [deleteCategoryFlagger]);

  const categoryIsRemovingId = deleteCategoryFlagger && deleteCategoryFlagger.isLoading 
    ? deleteCategoryFlagger.categoryId
    : null;

  return {
    categories, defineOrder, search, handleSearchChange, handleSearchClear, 
    handleSortOrderChange, handleAddCategory, onViewProductsClick, onCategoryUpdate, onCategoryDuplicate,
    handleDragEnd, handleSubCategoryDragEnd, setState, filteredCategories, categoriesSortOrder, 
    deleteCategory, setModals, modals, categoryIsRemovingId, clientId, isLoadingCategories,
    handleOrderChange, selectedOrder: order, deleteCategoryPromptFlagger, setDeleteCategoryPromptFlagger,
    isLoadingDeleteCategory, refreshData,
  }
}

export default useTechnologySpendState;
